<template>
  <div class="max-w-[90vw] mx-auto pb-10">
    <!-- Header Area -->
    <div class="w-full flex justify-between items-start mb-4 mt-6">
      <div>
        <a
          @click.prevent="goBack"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex mb-2"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold text-4xl text-gray-800">Bug #{{ bugID }}</h1>
        <div class="text-lg mt-1 flex flex-col text-gray-600">
          <span v-if="projeto" class="font-bold">
            Projeto: <span class="font-medium">{{ projeto }}</span>
          </span>
        </div>
      </div>

      <!-- Ações do Bug -->
      <div v-if="bug" class="flex flex-col items-end gap-3.5 mt-2.5">
        <!-- Ações do Dev -->
        <div
          v-if="isDev && bug.status_bug === 'EM_ANALISE'"
          class="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex flex-col items-end"
        >
          <div class="text-sm text-gray-600 mb-2 font-medium">
            O que deseja fazer com este bug?
          </div>
          <div class="flex gap-2">
            <ModalCriarTarefaCorrecao
              @tarefa-criada="recarregaPagina"
              :id="bugID"
              color="blue-darken-1"
              text="Aceitar"
            />
            <ModalNegarBug @bug-negado="recarregaPagina" :id="bugID" />
          </div>
        </div>

        <!-- Ações do QA: Para Validação -->
        <div
          v-if="isQA && bug.status_bug === 'PARA_VALIDACAO'"
          class="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex flex-col items-end"
        >
          <div class="text-sm text-gray-600 mb-2 font-medium">
            Este bug aguarda a sua validação. A solução foi satisfatória?
          </div>
          <div class="flex gap-2">
            <ModalAceitarAvaliacao
              @bug-realizado="recarregaPagina"
              :id="bugID"
            />
            <ModalRejeitarAvalicao
              @bug-para-fazer="recarregaPagina"
              :id="bugID"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="py-12 text-center text-gray-500 font-medium bg-white rounded-lg shadow-sm"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        class="mb-4"
        size="64"
      ></v-progress-circular>
      <p>Carregando dados do bug...</p>
    </div>

    <!-- Bug Content -->
    <div v-else-if="bug">
      <div
        class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <!-- Header info badges -->
        <div class="bg-slate-50 border-b border-gray-100 p-5">
          <div class="flex flex-wrap gap-3">
            <v-chip
              color="indigo-darken-1"
              variant="tonal"
              class="font-weight-medium"
            >
              <v-icon start size="small">mdi-bug</v-icon>
              Tipo: {{ formatarString(bug.tipo_bug) }}
            </v-chip>

            <v-chip
              :color="getGravidadeColor(bug.gravidade)"
              variant="tonal"
              class="font-weight-medium"
            >
              <v-icon start size="small">mdi-alert-circle</v-icon>
              Gravidade: {{ formatarString(bug.gravidade) }}
            </v-chip>

            <v-chip
              :color="getStatusColor(bug.status_bug)"
              variant="tonal"
              class="font-weight-medium"
            >
              <v-icon start size="small">mdi-list-status</v-icon>
              Status: {{ formatarString(bug.status_bug) }}
            </v-chip>

            <v-chip
              v-if="bug.origem_bug"
              :color="origemBugColor"
              variant="tonal"
              :prepend-icon="origemBugIcon"
              class="font-weight-medium"
            >
              {{ origemBugLabel }}
            </v-chip>
          </div>
        </div>

        <!-- QA: Justificativa de Negado -->
        <div
          v-if="isQA && bug.status_bug === 'NEGADO'"
          class="p-6 border-b border-red-100 bg-red-50"
        >
          <div class="flex items-start gap-4">
            <v-icon color="red-darken-2" size="32" class="mt-1"
              >mdi-alert-octagon</v-icon
            >
            <div class="flex-1">
              <h3 class="text-red-900 font-bold text-lg mb-1">
                Bug Negado pela Equipe de Desenvolvimento
              </h3>
              <p class="text-red-800 text-sm mb-4 whitespace-pre-wrap">
                {{ bug.justificativa }}
              </p>

              <div
                class="bg-white p-4 rounded-lg border border-red-200 inline-block"
              >
                <div class="text-sm text-gray-700 mb-3 font-medium">
                  A justificativa é aceitável?
                </div>
                <div class="flex gap-3">
                  <ModalAceitarJustificativa
                    @bug-cancelado="recarregaPagina"
                    :id="bugID"
                  />
                  <ModalCriarTarefaCorrecao
                    @tarefa-criada="recarregaPagina"
                    :id="bugID"
                    color="red-darken-1"
                    text="Não"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Details -->
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3
                class="text-lg font-bold text-[#004984] mb-3 flex items-center"
              >
                <v-icon class="mr-2">mdi-text-box-outline</v-icon>
                Descrição
              </h3>
              <div
                class="min-h-[120px] border border-[#c2cbd6] border-l-[4px] border-l-[#004984] bg-[#f7fafc] rounded p-4 text-[#2d3748] text-[14px] leading-[1.6] whitespace-pre-wrap"
              >
                {{ bug.descricao || 'Nenhuma descrição fornecida.' }}
              </div>
            </div>

            <div>
              <h3
                class="text-lg font-bold text-[#004984] mb-3 flex items-center"
              >
                <v-icon class="mr-2">mdi-note-text-outline</v-icon>
                Observação
              </h3>
              <div
                class="min-h-[120px] border border-[#e2e8f0] bg-gray-50 rounded p-4 text-gray-600 text-[14px] leading-[1.6] whitespace-pre-wrap"
              >
                {{ bug.observacao || 'Nenhuma observação fornecida.' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Descrições de Bugs Redundantes -->
        <div
          v-if="bug?.tb_bugs_redundantes?.length > 0"
          class="p-6 border-t border-gray-100 bg-[#f8fafc]"
        >
          <h3 class="text-lg font-bold text-[#004984] mb-4 flex items-center">
            <v-icon class="mr-2">mdi-text-box-multiple-outline</v-icon>
            Descrições Adicionais (Bugs Redundantes)
          </h3>
          <div class="space-y-4">
            <div
              v-for="red in bug.tb_bugs_redundantes"
              :key="red.id_bug"
              class="p-4 border border-[#e2e8f0] bg-white rounded shadow-sm"
            >
              <div class="mb-2 text-sm text-gray-500 font-medium">
                Registrado por {{ red.tb_usuario?.nome || 'Usuário' }}
                <v-chip
                  v-if="red.origem_bug"
                  size="x-small"
                  class="ml-2"
                  variant="tonal"
                  >{{ formatarString(red.origem_bug) }}</v-chip
                >
              </div>
              <div
                class="text-[#2d3748] text-[14px] leading-[1.6] whitespace-pre-wrap"
              >
                {{ red.descricao }}
              </div>
            </div>
          </div>
        </div>

        <!-- Evidências (Imagens e Vídeos) -->
        <div class="p-6 border-t border-gray-100 bg-slate-50">
          <h3 class="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <v-icon class="mr-2 text-warning">mdi-paperclip</v-icon> Evidências
          </h3>

          <v-tabs
            v-model="tabEvidencias"
            bg-color="transparent"
            color="primary"
            class="mb-4"
          >
            <v-tab value="imagens">Imagens ({{ imageURL.length }})</v-tab>
            <v-tab value="videos">Vídeos ({{ videoURL.length }})</v-tab>
          </v-tabs>

          <v-window v-model="tabEvidencias">
            <v-window-item value="imagens">
              <div
                v-if="imageURL.length === 0"
                class="text-gray-500 py-6 text-center bg-white rounded-lg border border-dashed border-gray-300"
              >
                Nenhuma imagem anexada a este bug.
              </div>
              <div
                v-else
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                <div
                  v-for="(link, index) in imageURL"
                  :key="index"
                  class="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm transition-transform hover:scale-[1.02]"
                >
                  <a :href="link" target="_blank" title="Abrir imagem original">
                    <div
                      class="h-[250px] w-full bg-gray-100 flex items-center justify-center overflow-hidden"
                    >
                      <img
                        :src="link"
                        alt="Evidência"
                        class="max-w-full max-h-full object-contain"
                      />
                    </div>
                  </a>
                </div>
              </div>
            </v-window-item>

            <v-window-item value="videos">
              <div
                v-if="videoURL.length === 0"
                class="text-gray-500 py-6 text-center bg-white rounded-lg border border-dashed border-gray-300"
              >
                Nenhum vídeo anexado a este bug.
              </div>
              <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div
                  v-for="(link, index) in videoURL"
                  :key="index"
                  class="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm p-4"
                >
                  <video
                    :src="link"
                    controls
                    class="w-full rounded-md bg-black"
                    style="max-height: 400px"
                  ></video>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @module DetalhesBug
 * @desc Mostra os detalhes de um bug com uma interface moderna
 */
import axios from 'axios';

import ModalAceitarAvaliacao from '@/components/dialogs/ModalAceitarAvaliacao.vue';
import ModalAceitarJustificativa from '@/components/dialogs/ModalAceitarJustificativa.vue';
import ModalCriarTarefaCorrecao from '@/components/dialogs/ModalCriarTarefaCorrecao.vue';
import ModalNegarBug from '@/components/dialogs/ModalNegarBug.vue';
import ModalRejeitarAvalicao from '@/components/dialogs/ModalRejeitarAvalicao.vue';
import store from '@/store/store';

export default {
  name: 'DetalhesBug',
  components: {
    ModalCriarTarefaCorrecao,
    ModalNegarBug,
    ModalAceitarAvaliacao,
    ModalRejeitarAvalicao,
    ModalAceitarJustificativa,
  },
  data() {
    return {
      projeto: '',
      bug: null,
      isLoading: false,
      bugID: 0,
      imageURL: [],
      videoURL: [],
      tabEvidencias: 'imagens',
    };
  },
  created() {
    this.projeto = localStorage.getItem('projeto');
    this.bugID = Number(this.$route.params.id);
    this.getBug();
    this.getImage();
    this.getVideo();
  },
  computed: {
    isDev() {
      return localStorage.getItem('formaAtuacao') === '"DESENVOLVEDOR"';
    },

    isQA() {
      return localStorage.getItem('formaAtuacao') === '"TESTADOR"';
    },

    origemBugLabel() {
      const origem = this.bug?.origem_bug;
      if (origem === 'TESTES_DOS_USUARIOS') {
        return 'Teste de Usuário';
      }
      if (origem === 'VALIDACAO') {
        return 'Validação';
      }

      return origem || '';
    },

    origemBugColor() {
      const origem = this.bug?.origem_bug;
      if (origem === 'TESTES_DOS_USUARIOS') {
        return 'deep-purple';
      }
      if (origem === 'VALIDACAO') {
        return 'teal';
      }

      return 'grey';
    },

    origemBugIcon() {
      const origem = this.bug?.origem_bug;
      if (origem === 'TESTES_DOS_USUARIOS') {
        return 'mdi-account-voice';
      }
      if (origem === 'VALIDACAO') {
        return 'mdi-check-decagram';
      }

      return 'mdi-information';
    },
  },
  methods: {
    async getBug() {
      try {
        this.isLoading = true;
        const apiUrl = process.env.VUE_APP_API_URL;
        const response = await axios.get(`${apiUrl}/api/v1/bug/${this.bugID}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        });
        this.bug = response.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    formatarString(string) {
      if (!string) {
        return '';
      }
      const palavras = string.split('_');

      return palavras
        .map(
          (palavra) =>
            palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase(),
        )
        .join(' ');
    },

    getGravidadeColor(gravidade) {
      const colors = {
        BLOQUEADORA: 'red-darken-3',
        CRITICA: 'red',
        MEDIA: 'orange',
        BAIXA: 'yellow-darken-3',
        TRIVIAL: 'green-darken-1',
      };

      return colors[gravidade] || 'grey';
    },

    getStatusColor(status) {
      const colors = {
        EM_ANALISE: 'info',
        PARA_FAZER: 'warning',
        NEGADO: 'error',
        PARA_VALIDACAO: 'primary',
        REALIZADO: 'success',
        CANCELADO: 'grey',
      };

      return colors[status] || 'grey';
    },

    getImage() {
      const apiUrl = process.env.VUE_APP_API_URL;
      axios
        .get(`${apiUrl}/api/v1/bug/arquivo/imagem/${this.bugID}`)
        .then((response) => {
          const imagem = response.data.imagem;
          for (let i = 0; i < imagem.length; i++) {
            this.imageURL.push(imagem[i].localizacao);
          }
        })
        .catch((error) => console.error(error));
    },

    getVideo() {
      const apiUrl = process.env.VUE_APP_API_URL;
      axios
        .get(`${apiUrl}/api/v1/bug/arquivo/video/${this.bugID}`)
        .then((response) => {
          const video = response.data.video;
          for (let i = 0; i < video.length; i++) {
            this.videoURL.push(video[i].localizacao);
          }
        })
        .catch((error) => console.error(error));
    },

    recarregaPagina() {
      this.projeto = localStorage.getItem('projeto');
      this.bugID = Number(this.$route.params.id);
      this.getBug();
    },

    goBack() {
      // Retorna para a lista de bugs do projeto atual
      const idProjeto = this.$route.params.idProjeto;
      this.$router.push(`/projects/${idProjeto}/Bugs/`);
    },
  },
};
</script>
